import {presentationValue, type ToolPresentation} from "@repo/ui/gailgpt/tool-presentation";

export type ApprovalReferenceKind = "script" | "textScript" | "tool" | "useCase" | "campaign" | "contact" | "contactList" | "sequence" | "redialingRule" | "assistant" | "document" | "eval";

/** Resource identity comes from documented fields, never from a string looking like a UUID. */
const fields: Readonly<Record<string, ApprovalReferenceKind>> = {
   scriptId: "script", scriptIds: "script", phoneScriptId: "script", defaultScriptId: "script", sequenceScripts: "script",
   textScriptId: "textScript", textScriptIds: "textScript",
   toolId: "tool", toolIds: "tool", toolAssociations: "tool",
   useCaseId: "useCase", useCaseIds: "useCase", useCaseAssociations: "useCase",
   campaignId: "campaign", campaignIds: "campaign",
   contactId: "contact", contactIds: "contact", contactListId: "contactList", contactListIds: "contactList",
   sequenceId: "sequence", sequenceIds: "sequence", redialingRuleId: "redialingRule", redialingRules: "redialingRule",
   assistantId: "assistant", assistantIds: "assistant", documentId: "document", documentIds: "document",
   evalId: "eval",
};
const targets: Readonly<Record<string, ApprovalReferenceKind>> = {
   phone_scripts: "script", text_scripts: "textScript", http_tools: "tool", use_cases: "useCase",
   campaigns: "campaign", contacts: "contact", contact_lists: "contactList", sequences: "sequence",
   redialing_rules: "redialingRule", assistants: "assistant", documents: "document",
   script_evals: "eval",
};

export function approvalReferenceKind(path: readonly string[], toolName?: string): ApprovalReferenceKind | undefined {
   const properties = path.filter(segment => !/^\d+$/.test(segment));
   const field = properties.at(-1);
   if (!field) return;
   if (isBulkContactTarget(path, toolName)) return "contact";
   if (field !== "id") return Object.hasOwn(fields, field) ? fields[field] : undefined;
   const parent = properties.at(-2);
   if (parent && Object.hasOwn(fields, parent)) return fields[parent];
   if (toolName && (properties.length === 1 || (properties.length === 2 && properties[0] === "body"))) {
      return Object.entries(targets).find(([resource]) => toolName.startsWith(`${resource}_`))?.[1];
   }
}

/** Only the documented bulk-archive target list gives a bare `ids` field a contact identity. */
export function isBulkContactTarget(path: readonly string[], toolName?: string) {
   const properties = path.filter(segment => !/^\d+$/.test(segment));
   return toolName === "contacts_bulk_archive" && (
      (properties.length === 1 && properties[0] === "ids") ||
      (properties.length === 2 && properties[0] === "body" && properties[1] === "ids")
   );
}

export const referenceKey = (kind: ApprovalReferenceKind, id: string) => JSON.stringify([kind, id]);

/** Custom layouts may regroup fields; only unambiguous frozen labels may follow a typed ID. */
export function frozenReferenceNames(presentation: ToolPresentation, parameters: Record<string, unknown>) {
   const names = new Map<string, string | null>();
   for (const field of presentation.fields) {
      const kind = approvalReferenceKind(field.path, presentation.toolName);
      const {value} = presentationValue(parameters, field.path);
      if (!kind || field.format !== "entity" || !field.entityName || typeof value !== "string") continue;
      const key = referenceKey(kind, value);
      if (!names.has(key)) names.set(key, field.entityName);
      else if (names.get(key) !== field.entityName) names.set(key, null);
   }
   return names;
}

/** Request paths address presentation metadata; snapshot paths only identify reference types. */
export type ApprovalValuePath = {source: "request"; segments: readonly string[]} | {source: "snapshot"; segments: readonly string[]};
export type ApprovalReference = {id: string; kind?: ApprovalReferenceKind; name?: string; path?: ApprovalValuePath};
export type ApprovalReferenceContext = {
   toolName?: string;
   scriptId?: string;
   names: ReadonlyMap<string, string | null>;
   paths: ReadonlyMap<string, {id: string; name: string}>;
};
/** A required lookup reads an action's target, which the review must identify, not an optional label. */
type ReferenceResolution = {status: "resolved"; name: string} | {status: "unavailable"} | {status: "lookup"; kind: ApprovalReferenceKind; required: boolean};

/** A conflicting frozen label is unavailable, never permission to fetch a newer label. */
export function resolveApprovalReference({id, kind, name, path}: ApprovalReference, context: ApprovalReferenceContext): ReferenceResolution {
   const exact = path?.source === "request" ? context.paths.get(JSON.stringify(path.segments)) : undefined;
   if (exact?.id === id) return {status: "resolved", name: exact.name};
   if (kind) {
      const key = referenceKey(kind, id);
      if (context.names.has(key)) {
         const frozen = context.names.get(key);
         return frozen ? {status: "resolved", name: frozen} : {status: "unavailable"};
      }
   }
   if (name) return {status: "resolved", name};
   if (!kind || !id) return {status: "unavailable"};
   return {status: "lookup", kind, required: path?.source === "request" && isBulkContactTarget(path.segments, context.toolName)};
}
